/**
 * Security policies section.
 *
 * Extracted from AdminSettingsPage.tsx (Sub-PR 6.3).
 */

import { useTranslation } from 'react-i18next'
import { SettingCard } from './SettingCard'
import { Toggle } from './Toggle'
import type { AdminSettings } from './types'

const DECIMAL_BASE = 10
const DEFAULT_MAX_LOGIN_ATTEMPTS = 5

interface SecuritySectionProps {
  isOrgAdmin: boolean
  settings: AdminSettings
  setSettings: (s: AdminSettings) => void
}

export function SecuritySection({
  isOrgAdmin,
  settings,
  setSettings,
}: SecuritySectionProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <SettingCard
      title={
        isOrgAdmin ? t('adminSettings.orgSecurityPolicies') : t('adminSettings.securityPolicies')
      }
      description={
        isOrgAdmin
          ? t('adminSettings.orgSecurityPoliciesDesc')
          : t('adminSettings.securityPoliciesDesc')
      }
    >
      <div className="space-y-4">
        <Toggle
          enabled={settings.enforceStrongPasswords}
          onChange={(value) => {
            setSettings({ ...settings, enforceStrongPasswords: value })
          }}
          label={t('adminSettings.enforceStrongPasswords')}
        />
        <Toggle
          enabled={settings.enableAuditLogging}
          onChange={(value) => {
            setSettings({ ...settings, enableAuditLogging: value })
          }}
          label={t('adminSettings.enableAuditLogging')}
        />
        <div className="border-t border-stone-200 pt-4 dark:border-stone-700">
          <label className="mb-2 block text-sm font-medium text-stone-700 dark:text-stone-300">
            {t('adminSettings.maxLoginAttempts')}
          </label>
          <input
            type="number"
            min="3"
            max="10"
            value={settings.maxLoginAttempts}
            onChange={(e) => {
              setSettings({
                ...settings,
                maxLoginAttempts:
                  parseInt(e.target.value, DECIMAL_BASE) || DEFAULT_MAX_LOGIN_ATTEMPTS,
              })
            }}
            className="w-32 rounded-lg border border-stone-300 bg-white px-3 py-2 text-stone-900 dark:border-stone-600 dark:bg-stone-700 dark:text-stone-100"
          />
          <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
            {t('adminSettings.accountLockedMessage')}
          </p>
        </div>
      </div>
    </SettingCard>
  )
}
